<template>
	<div id="app" v-if="ready" :class="{msEdge: isMsEdge}">
		<div class="screen" :style="{'background-image':'url('+ game_config.ui_wallpaper +')'}">
			<div class="screen-background">
				<div class="screen-area">
					<transition
						type="transition"
						:name="transitionName"
						:mode="transitionMode"
						@before-enter="isTransitioning = true"
						@after-enter="isTransitioning = false"
						>
						<router-view/>
					</transition>
					<div v-if="allowFullscreen" class="fullscreen-btn" @click="toggle">
						<img :src="game_config.exit_fullscreen_image" v-if="fullscreen" alt="Exit Fullscreen."/>
						<img :src="game_config.enter_fullscreen_image" v-else alt="Enter Fullscreen." />
					</div>
					<InfoButton></InfoButton>
					<Info v-if="infoOpen"></Info>
					<Vignette></Vignette>
				</div>
			</div>
		</div>
		<RestrictOrientation></RestrictOrientation>
	</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import fullscreen from 'vue-fullscreen';
import Vue from 'vue';
import transitionSettings from '@/mixins/transition-settings';
import Info from '@/components/abstract/Info';
import InfoButton from '@/components/abstract/InfoButton';
import RestrictOrientation from '@/components/abstract/RestrictOrientation';
import Vignette from '@/components/abstract/Vignette';

Vue.use(fullscreen);

export default {
	name: 'App',
	mixins: [transitionSettings],
	data() {
		return {
			isTransitioning: false,
			isMsEdge: false,
			fullscreen: false,
		};
	},
	components: {
		Info,
		InfoButton,
		RestrictOrientation,
		Vignette,
	},
	computed: {
		...mapState({
			ready: state => state.config.loaded,
			view: state => state.currentView,
			game_config: state => state.config.data.game_config,
		}),
		infoOpen() {
			return this.$store.state.infoOpen;
		},
		allowFullscreen() { // Warning: Hack Alert! apple does not support fullscreen api so hide the fullscreen button
			const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
			// console.log(`iOS: ${iOS}`);
			if (iOS === false) { // if not iOS
				return true; // allow fullscreen
			}
			return false; // else hide it
		},
		transitionName() {
			let transitionName = "";
			switch (this.view) {
			/* case "AssetLoader":
			case "IntroScreen":
			case "StageIntro": */
			case "Title":
			case "Game":
				transitionName = "fade";
				break;
			/* case "GameOver":
				transitionName = "swipe";
				break; */
			default:
				transitionName = "fade";
				break;
			}

			return transitionName;
		},
		transitionMode() {
			let transitionMode = "";

			switch (this.view) {
			case "Title":
				transitionMode = "out-in";
				break;
			default:
				transitionMode = "";
				break;
			}

			return transitionMode;
		},
	},
	methods: {
		...mapActions([
			"getAppConfig",
		]),
		toggle() {
			this.$fullscreen.toggle(this.$el.querySelector('.screen'), {
				wrap: false,
				callback: this.fullscreenChange,
			});
		},
		fullscreenChange(fs) {
			this.fullscreen = fs;
		},
	},
	created() {
		this.$router.replace({ path: "/", query: window.location.search });
		this.getAppConfig();
		if (/Edge/.test(navigator.userAgent)) {
			console.log('Hello Microsoft Edge User!');
			this.isMsEdge = true;
		}
	},
	mounted() {
	},
};
</script>
<style lang="scss">
	@import 'assets/styles/utilities/**/*.scss';

	html, body {
		height: 100%;
		font-family: $body-font-stack;
		font-size: 16px;
		letter-spacing: 0.01em;
		box-sizing: border-box;
		margin: 0;
		padding: 0;
		-webkit-filter: blur(0px); // make pixel images plxelated, not blurry for Safari
		image-rendering: auto; // make pixel images pixelated, not blurry for modern browsers
		image-rendering: crisp-edges;
		image-rendering: pixelated;
		// -webkit-font-smoothing: none; // make pixel fonts smooth
		// -moz-osx-font-smoothing: unset;
		// font-smooth: never;
	}
	*, *:before, *:after {
		box-sizing: inherit;
		position: relative;
	}
	#app{
		position: fixed;
		width: 100%;
		height: 100%;
		overflow: hidden;
		-webkit-filter: blur(0px);
	}
	input, textarea, select, button{
		font-family: $body-font-stack;
		font-size: 16px;
		text-align: center;
	}
	.screen{
		text-align: center;
		color: #2c3e50;
		position: absolute;
		width: 100%;
		height: 100%;
		-webkit-filter: blur(0px);
		background-size: 10% auto;
		background-repeat: repeat;
		background-position: top left;
	}
	.screen-background {
		position: absolute;
		overflow: hidden;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.screen-area {
		box-shadow: 0 0 0 5px black;
		@include breakpoint(sm){
			box-shadow: 0 0 0 4px black;
		}
		@include breakpoint(xs){
			box-shadow: 0 0 0 3px black;
		}
		@media screen and (max-aspect-ratio: 111/64) {
			width: 100vw;
			height: 57.6vw;
			overflow: hidden;
		}
		@media screen and (min-aspect-ratio: 111/64) {
			width: 173.4vh;
			height: 100vh;
		}
	}
	//
	.fullscreen-btn{
		width: 2.25%;
		min-width: 20px;
		position: absolute;
		left: 1%;
		top: 9%;
		z-index: 1000000;
		cursor: pointer;
		img{
			width: 100%;
			height: auto;
			transform: scale(1);
		}
		&:hover{
			cursor: pointer;
			img{
				transform: scale(1.1);
			}
		}
	}
	//
	// 1024x590 sized viewport for MS EDGE b/c it does not support scaling pixel images
	#app.msEdge{
		background-size: 130px auto;
		.screen-background{
			height: 600px;
			border-top: 5px solid black;
			border-bottom: 5px solid black;
			top: 50%;
			transform: translateY(-50%);
		}
		.screen-area{
			width: 1024px;
			height: 600px;
			box-shadow: none;
			@media screen and (max-aspect-ratio: 111/64) {
				width: 1024px;
				height: 590px;
				overflow: visible;
			}
			@media screen and (min-aspect-ratio: 111/64) {
				width: 1024px;
				height: 590px;
			}
		}
		.button{
			font-size: 1.06em;
			border-width: 5px;
			margin: 15px auto;
		}
		.startOverBtn{
			margin-right: 30px;
		}
		.component-container{
			width: 1024px;
			height: 590px;
			.wall{
				width: 200vw;
			}
			.topBar{
				width: 200vw;
			}
			.totalMoney{
				font-size: 2em;
			}
			.door-alert .knock p{
				font-size: 1.5em;
			}
			.email{
				width: 68%;
				.email-content{
					padding: 40px 40px;
					.email-header{
						font-size: 1em;
						text-align: left;
						padding: 10px 10px 10px 0;
					}
					.email-body{
						font-size: 1.125em;
						text-align: left;
						padding: 10px 0px 10px 0;
					}
				}
			}
			.request{
				box-shadow: 0 0 0 5px black, 0 0 0 10px $purple, 0 0 0 15px black;
				padding: 0.5% 1.5%;
				.body{
					font-size: 1.375em;
					line-height: 1.3em;
				}
			}
			.hint-btn{
				width: 90px;
				height: 34px;
			}
			.hint-content p{
				font-size: 1em;
			}
			.card-face{
				font-size: 16px;
				.card-term{
					font-size: 1.5em;
					line-height: 1em;
				}
				.card-heading{
					font-size: 0.75em;
					margin-top: 0.75em;
				}
				&.returner{
					.card-term{
						font-size: 1.25em;
						line-height: 1.3em;
						padding: 10px 0;
					}
				}
			}
		}
		.info-component-container{
			width: 1024px;
		}
	}
</style>



// WEBPACK FOOTER //
// src/App.vue